import * as THREE from 'three';

/**
 * AnimationController - A state-machine based animation system
 * 
 * This controller handles all animation logic for player models with proper
 * state transitions, priorities, and blend handling.
 */
class AnimationController {
  constructor() {
    // Animation state priorities (highest to lowest)
    this.PRIORITY = {
      DEATH: 100,
      JUMPING: 80,
      FALLING: 70,
      CROUCHING: 60,
      MOVING: 40,
      IDLE: 20
    };
    
    // Default animation blend durations - making these faster for better responsiveness
    this.blendDurations = {
      default: 0.15,     // Faster default blend (was 0.2)
      directional: 0.15, // Faster directional blends (was 0.3)
      running: 0.1,      // Even faster running transitions (was 0.15)
      idle: 0.15,        // Faster idle transition (was 0.3)
      instant: 0,        // No blending for instant transitions
      movement: 0.08     // Ultra-fast blending for movement state changes
    };
    
    // Lock time factor - what fraction of the blend duration to lock animations
    // Lower values = more responsive, higher values = fewer animation glitches
    this.lockTimeFactor = 0.25; // Lock for 25% of blend duration by default
    
    // Movement threshold for detecting state changes
    this.movementThreshold = 0.02;
    
    // Debug mode
    this.debug = false;
    
    // Keep track of the last frame time for smoother updates
    this.lastFrameTime = Date.now();
  }
  
  /**
   * Initialize controller for a specific player model
   * @param {THREE.Group} playerGroup - The player group with userData.animations
   * @param {Object} characterConfig - The character animation config
   * @param {string} playerId - Player ID for logging
   */
  initializePlayer(playerGroup, characterConfig, playerId) {
    if (!playerGroup || !playerGroup.userData) {
      this.logError(`Cannot initialize animations: Invalid player group for ${playerId}`);
      return false;
    }
    
    // Store player details
    playerGroup.userData.animController = {
      currentState: 'idle',
      previousState: null,
      priority: this.PRIORITY.IDLE,
      lastStateChange: Date.now(),
      isTransitioning: false,
      characterType: playerGroup.userData.character || 'soldier',
      playerId: playerId,
      initialized: true,
      lockUntil: 0
    };
    
    // Log animation setup
    this.logDebug(`Initializing animations for player ${playerId}`);
    
    // Check if animations are available
    if (!playerGroup.userData.animations || Object.keys(playerGroup.userData.animations).length === 0) {
      this.logError(`No animations available for player ${playerId}`);
      return false;
    }
    
    // Map animations according to the character config
    this.createAnimationMappings(playerGroup, characterConfig);
    
    // Start with idle animation
    return this.playAnimation(playerGroup, 'idle');
  }
  
  /**
   * Create mappings between animation keys and animation clips
   */
  createAnimationMappings(playerGroup, characterConfig) {
    if (!characterConfig || !characterConfig.animations) {
      this.logError(`No animation config provided for player ${playerGroup.userData.animController.playerId}`);
      return;
    }
    
    const availableAnims = Object.keys(playerGroup.userData.animations);
    this.logDebug(`Player has ${availableAnims.length} animations: ${availableAnims.join(', ')}`);
    
    // Create animation mappings in userData
    playerGroup.userData.animationMap = {};
    
    // Map each animation name to the actual animation clip
    Object.entries(characterConfig.animations).forEach(([key, animName]) => {
      // Check if this animation exists
      if (playerGroup.userData.animations[animName]) {
        playerGroup.userData.animationMap[key] = animName;
        this.logDebug(`Mapped animation key ${key} → ${animName}`);
      } else {
        this.logError(`Animation ${animName} not found for ${key} action`);
      }
    });
    
    // Log final animation mappings
    this.logDebug(`Animation mappings for ${playerGroup.userData.animController.playerId}:`, 
                  Object.entries(playerGroup.userData.animationMap).map(([k, v]) => `${k}→${v}`).join(', '));
  }
  
  /**
   * Get the correct animation state based on player conditions
   * @param {Object} conditions - All player state conditions
   * @returns {string} The animation state to use
   */
  determineAnimationState(conditions) {
    const {
      isDead,
      isJumping, 
      jumpVelocity,
      isCrouching, 
      isMoving,
      isRunning,
      moveDirection
    } = conditions;
    
    // Check states in priority order
    if (isDead) {
      return 'death';
    }
    
    if (isJumping) {
      return jumpVelocity > 0 ? 'jump' : 'fall';
    }
    
    if (isCrouching) {
      if (!isMoving) {
        return 'crouchIdle';
      }
      
      // Determine crouch movement animation based on direction
      switch (moveDirection) {
        case 'forward': return 'crouchForward';
        case 'backward': return 'crouchBackward';
        case 'left': return 'crouchLeft';
        case 'right': return 'crouchRight';
        default: return 'crouchIdle';
      }
    }
    
    // Regular standing animations
    if (isMoving) {
      switch (moveDirection) {
        case 'forward': return isRunning ? 'run' : 'walk';
        case 'backward': return 'walkBackward';
        case 'left': return 'walkLeft';
        case 'right': return 'walkRight';
        default: return 'walk';
      }
    }
    
    // Default to idle
    return 'idle';
  }
  
  /**
   * Get state priority for an animation
   * @param {string} state - Animation state name
   * @returns {number} Priority value
   */
  getStatePriority(state) {
    if (state.includes('death')) return this.PRIORITY.DEATH;
    if (state === 'jump') return this.PRIORITY.JUMPING;
    if (state === 'fall') return this.PRIORITY.FALLING;
    if (state.includes('crouch')) return this.PRIORITY.CROUCHING;
    if (state.includes('walk') || state.includes('run')) return this.PRIORITY.MOVING;
    return this.PRIORITY.IDLE;
  }
  
  /**
   * Determine the blend duration between two animation states
   * @param {string} fromState - Current animation state
   * @param {string} toState - Target animation state
   * @returns {number} Blend duration in seconds
   */
  getBlendDuration(fromState, toState) {
    // Immediate transitions
    if (toState.includes('death')) {
      return this.blendDurations.instant;
    }
    
    // Crouch toggle transitions should be faster
    if ((fromState === 'idle' && toState === 'crouchIdle') ||
        (fromState === 'crouchIdle' && toState === 'idle') ||
        (fromState?.includes('crouch') && fromState !== 'crouchIdle' && toState === 'crouchIdle')) {
      return this.blendDurations.movement; // Use the fast movement blend
    }
    
    // Special transition types
    if (toState === 'idle' || toState === 'crouchIdle') {
      return this.blendDurations.idle;
    }
    
    if (toState.includes('run') || fromState?.includes('run')) {
      return this.blendDurations.running;
    }
    
    if (['walkLeft', 'walkRight', 'walkBackward', 'crouchLeft', 'crouchRight', 'crouchBackward'].includes(toState)) {
      return this.blendDurations.directional;
    }
    
    return this.blendDurations.default;
  }
  
  /**
   * Update player animation based on current conditions
   * @param {THREE.Group} playerGroup - The player model
   * @param {Object} conditions - Player state conditions
   * @returns {boolean} Whether animation was updated
   */
  updateAnimation(playerGroup, conditions) {
    if (!playerGroup || !playerGroup.userData || !playerGroup.userData.animController) {
      return false;
    }
    
    const controller = playerGroup.userData.animController;
    const playerId = controller.playerId;
    
    // Determine the appropriate animation state
    const newState = this.determineAnimationState(conditions);
    const newPriority = this.getStatePriority(newState);
    const currentPriority = controller.priority;
    
    // Store the last crouch state to handle crouch toggle properly
    if (newState === 'crouchIdle' || newState.startsWith('crouch')) {
      playerGroup.userData.lastCrouchState = newState;
    }
    
    // Handle movement synchronization cases - these MUST be responsive immediately
    const isMovementChange = this.isMovementStateChange(controller.currentState, newState, playerGroup.userData);
    
    // Special handling for crouch state changes - they should be responsive
    const isCrouchStateChange = 
      (controller.currentState === 'idle' && newState === 'crouchIdle') ||
      (controller.currentState === 'crouchIdle' && newState === 'idle') ||
      (controller.currentState?.includes('crouch') && controller.currentState !== 'crouchIdle' && newState === 'crouchIdle');
    
    // Skip lock time completely for movement state changes and crouch toggles
    const skipLock = isMovementChange || 
                     isCrouchStateChange ||
                     (Date.now() >= controller.lockUntil);
    
    // Only change animation if one of these is true:
    // 1. It's a movement state change (must be responsive)
    // 2. It's a crouch state change (must be responsive)
    // 3. It's a different state AND the lock time has passed AND priority is sufficient
    if ((isMovementChange && newState !== controller.currentState) || 
        (isCrouchStateChange && newState !== controller.currentState) ||
        (skipLock && newState !== controller.currentState && newPriority >= currentPriority)) {
      this.logDebug(`Updating animation for ${playerId}: ${controller.currentState} → ${newState} (movement change: ${isMovementChange}, crouch change: ${isCrouchStateChange})`);
      return this.playAnimation(playerGroup, newState);
    }
    
    return false;
  }
  
  /**
   * Determine if this is a movement state change that requires immediate response
   * @param {string} currentState - Current animation state 
   * @param {string} newState - Target animation state
   * @param {Object} playerData - Optional player data to check actual position
   * @returns {boolean} True if this is a critical movement change
   */
  isMovementStateChange(currentState, newState, playerData) {
    // Check animation state changes first (most reliable)
    // Moving to idle is critical - must happen immediately when player stops
    if ((currentState?.includes('walk') || currentState?.includes('run') || 
         currentState?.includes('crouch') && currentState !== 'crouchIdle') && 
        (newState === 'idle' || newState === 'crouchIdle')) {
      return true;
    }
    
    // Starting to move from idle is also critical
    if ((currentState === 'idle' || currentState === 'crouchIdle') &&
        (newState?.includes('walk') || newState?.includes('run') || 
         newState?.includes('crouch') && newState !== 'crouchIdle')) {
      return true;
    }
    
    // Crouch state changes should be responsive
    if ((currentState === 'idle' && newState === 'crouchIdle') ||
        (currentState === 'crouchIdle' && newState === 'idle')) {
      return true;
    }
    
    // Direction changes while moving should be responsive
    if ((currentState?.includes('walk') || currentState?.includes('run') || 
         currentState?.includes('crouch') && currentState !== 'crouchIdle') &&
        (newState?.includes('walk') || newState?.includes('run') || 
         newState?.includes('crouch') && newState !== 'crouchIdle') &&
        currentState !== newState) {
      return true;
    }
    
    // If we have player data with position info, check for actual movement
    if (playerData && playerData.position && playerData.lastPosition) {
      const currentPos = playerData.position;
      const lastPos = playerData.lastPosition;
      
      // Calculate distance moved
      const dx = currentPos.x - lastPos.x;
      const dz = currentPos.z - lastPos.z;
      const distance = Math.sqrt(dx*dx + dz*dz);
      
      // If movement is above threshold, it's a movement change
      if (distance > this.movementThreshold) {
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * Play a specific animation with proper state transitions
   * @param {THREE.Group} playerGroup - The player model
   * @param {string} animationState - The animation state name
   * @returns {boolean} Whether the animation was successfully played
   */
  playAnimation(playerGroup, animationState) {
    if (!playerGroup || !playerGroup.userData) {
      return false;
    }
    
    const controller = playerGroup.userData.animController;
    if (!controller) {
      this.logError('No animation controller found on player');
      return false;
    }
    
    const playerId = controller.playerId || 'unknown';
    
    // Get the actual animation name from the mapping
    const animName = playerGroup.userData.animationMap?.[animationState];
    
    // If no mapping found, try to find a fallback
    if (!animName) {
      this.logError(`No mapping found for animation state: ${animationState}`);
      
      // Try partial matching as a last resort
      const fallbackAnimation = this.findFallbackAnimation(playerGroup, animationState);
      if (fallbackAnimation) {
        this.logDebug(`Using fallback animation for ${animationState}: ${fallbackAnimation}`);
        return this.playActualAnimation(playerGroup, fallbackAnimation, animationState);
      }
      
      return false;
    }
    
    // Play the mapped animation
    return this.playActualAnimation(playerGroup, animName, animationState);
  }
  
  /**
   * Actually play the animation with the given name
   */
  playActualAnimation(playerGroup, animName, stateKey) {
    const controller = playerGroup.userData.animController;
    const playerId = controller.playerId || 'unknown';
    
    // Get the animation action
    const action = playerGroup.userData.animations[animName];
    if (!action) {
      this.logError(`Animation action not found: ${animName}`);
      return false;
    }
    
    // Determine if this is a movement state change (requires immediate response)
    const isMovementChange = this.isMovementStateChange(controller.currentState, stateKey, playerGroup.userData);
    
    // Check for crouch state changes
    const isCrouchStateChange = 
      (controller.currentState === 'idle' && stateKey === 'crouchIdle') ||
      (controller.currentState === 'crouchIdle' && stateKey === 'idle') ||
      (controller.currentState?.includes('crouch') && controller.currentState !== 'crouchIdle' && stateKey === 'crouchIdle');
    
    // For movement or crouch changes, immediately stop other animations
    if ((isMovementChange || isCrouchStateChange) && playerGroup.userData.mixer) {
      // Get all running actions
      const runningActions = playerGroup.userData.mixer._actions.filter(a => a.isRunning());
      
      // Stop all other actions immediately
      runningActions.forEach(runningAction => {
        if (runningAction !== action) {
          if (stateKey === 'idle' || stateKey === 'crouchIdle') {
            // For stopping movement, use a very fast fadeout
            runningAction.fadeOut(0.05);
          } else {
            // For starting movement, use slightly longer fadeout
            runningAction.fadeOut(0.08);
          }
        }
      });
    } 
    // For non-movement transitions, use a gentler fadeout
    else if (playerGroup.userData.mixer) {
      // Get all running actions
      const runningActions = playerGroup.userData.mixer._actions.filter(a => a.isRunning());
      
      // Stop all other actions with appropriate fadeout
      runningActions.forEach(runningAction => {
        if (runningAction !== action) {
          runningAction.fadeOut(0.1);
        }
      });
    }
    
    // Calculate blend duration based on transition type
    let blendDuration;
    
    if (isMovementChange || isCrouchStateChange) {
      // Movement and crouch changes get their special faster blend duration
      blendDuration = this.blendDurations.movement;
    } else {
      // Other animations use their regular blend durations
      blendDuration = this.getBlendDuration(controller.currentState, stateKey);
    }
    
    // Update state before playing to prevent recursion issues
    controller.previousState = controller.currentState;
    controller.currentState = stateKey;
    controller.priority = this.getStatePriority(stateKey);
    controller.lastStateChange = Date.now();
    controller.isTransitioning = blendDuration > 0;
    
    // Calculate lock time - much shorter for movement and crouch transitions
    if (blendDuration > 0) {
      // For movement or crouch changes, use minimal lock time
      if (isMovementChange || isCrouchStateChange) {
        controller.lockUntil = Date.now() + 25; // Just 25ms lock for immediate changes
      } else {
        // Use lockTimeFactor to determine lock duration
        controller.lockUntil = Date.now() + (blendDuration * 1000) * this.lockTimeFactor;
      }
    }
    
    // Reset and play the new animation with proper weight management
    action.reset();
    action.setEffectiveTimeScale(1.0);
    action.setEffectiveWeight(1.0);
    
    // For movement changes, start the animation in a non-zero frame to avoid initial pose
    if (isMovementChange && stateKey !== 'idle' && stateKey !== 'crouchIdle') {
      // Start slightly into the animation for smoother transitions
      action.time = 0.05;
    }
    
    action.play();
    
    // If we have a previous animation, crossfade to the new one
    if (controller.previousState && 
        playerGroup.userData.animationMap && 
        playerGroup.userData.animationMap[controller.previousState]) {
      const prevAnimName = playerGroup.userData.animationMap[controller.previousState];
      const previousAction = playerGroup.userData.animations[prevAnimName];
      
      if (previousAction && previousAction !== action && blendDuration > 0) {
        this.logDebug(`Crossfading ${controller.previousState} → ${stateKey} with duration: ${blendDuration}s`);
        action.crossFadeFrom(previousAction, blendDuration, true);
      }
    }
    
    // Set timeScale for running animations to make them slightly faster
    if (stateKey === 'run') {
      action.timeScale = 1.5;
    } else {
      action.timeScale = 1.0;
    }
    
    // Update player userData for the animation
    playerGroup.userData.currentAnimation = stateKey;
    
    this.logDebug(`Animation changed for ${playerId}: ${controller.previousState} → ${stateKey} (movement: ${isMovementChange ? 'yes' : 'no'})`);
    return true;
  }
  
  /**
   * Find a fallback animation if the requested one isn't available
   */
  findFallbackAnimation(playerGroup, requestedState) {
    // Fallback mappings
    const fallbacks = {
      'death': ['death', 'idleDeath', 'runDeath', 'rifle_idle_death', 'rifle_run_death'],
      'crouchIdle': ['crouchIdle', 'crouch_idle', 'rifle_crouch_idle', 'idle'],
      'crouchForward': ['crouchForward', 'crouchFwd', 'rifle_crouch_fwd', 'crouchIdle'],
      'crouchBackward': ['crouchBackward', 'crouchBwd', 'rifle_crouch_bwd', 'crouchIdle'],
      'crouchLeft': ['crouchLeft', 'rifle_crouch_left', 'crouchIdle'],
      'crouchRight': ['crouchRight', 'rifle_crouch_right', 'crouchIdle'],
      'idle': ['idle', 'rifle_idle'],
      'walk': ['walk', 'rifle_run_fwd', 'rifle_walk_fwd', 'idle'],
      'run': ['run', 'rifle_run_fwd', 'walk'],
      'walkBackward': ['walkBackward', 'walkBwd', 'rifle_run_bwd', 'walk'],
      'walkLeft': ['walkLeft', 'rifle_run_left', 'walk'],
      'walkRight': ['walkRight', 'rifle_run_right', 'walk'],
      'jump': ['jump', 'rifle_jump', 'idle'],
      'fall': ['fall', 'rifle_fall', 'jump', 'idle']
    };
    
    // Check if we have fallbacks for this state
    const candidates = fallbacks[requestedState] || [];
    if (candidates.length === 0) return null;
    
    // Try each fallback animation
    for (const candidate of candidates) {
      // Check in the animation map first
      if (playerGroup.userData.animationMap && playerGroup.userData.animationMap[candidate]) {
        const mappedName = playerGroup.userData.animationMap[candidate];
        if (playerGroup.userData.animations[mappedName]) {
          return mappedName;
        }
      }
      
      // Try direct animation name
      if (playerGroup.userData.animations[candidate]) {
        return candidate;
      }
    }
    
    // Last resort - return any animation
    const availableAnims = Object.keys(playerGroup.userData.animations);
    return availableAnims.length > 0 ? availableAnims[0] : null;
  }
  
  /**
   * Set the blend durations
   * @param {Object} durations - Object with blend duration values
   */
  setBlendDurations(durations) {
    if (durations.default !== undefined) this.blendDurations.default = durations.default;
    if (durations.directional !== undefined) this.blendDurations.directional = durations.directional;
    if (durations.running !== undefined) this.blendDurations.running = durations.running;
    if (durations.idle !== undefined) this.blendDurations.idle = durations.idle;
    if (durations.movement !== undefined) this.blendDurations.movement = durations.movement;
  }
  
  /**
   * Configure animation blending and other animation settings
   * @param {Object} config - Configuration object with various animation settings
   * @returns {boolean} Success
   */
  setAnimationConfig(config) {
    // Update blend durations
    if (config.blendDurations) {
      this.setBlendDurations(config.blendDurations);
    }
    
    // Update lock time factor (how long animations are locked after transition starts)
    if (config.lockTimeFactor !== undefined) {
      this.lockTimeFactor = Math.max(0, Math.min(1, config.lockTimeFactor));
    }
    
    // Movement detection sensitivity (lower = more responsive)
    if (config.movementThreshold !== undefined) {
      this.movementThreshold = Math.max(0.001, config.movementThreshold);
    }
    
    // Debug logging
    if (config.debug !== undefined) {
      this.debug = config.debug;
    }
    
    this.logDebug('Animation config updated:', {
      blendDurations: this.blendDurations,
      lockTimeFactor: this.lockTimeFactor, 
      movementThreshold: this.movementThreshold,
      debug: this.debug
    });
    
    return true;
  }
  
  /**
   * Enable or disable debug mode
   */
  setDebug(enabled) {
    this.debug = enabled;
  }
  
  /**
   * Log debug messages if debug mode is enabled
   */
  logDebug(message, ...args) {
    if (this.debug) {
      console.log(`[AnimController] ${message}`, ...args);
    }
  }
  
  /**
   * Log errors (always shown)
   */
  logError(message, ...args) {
    console.error(`[AnimController] ERROR: ${message}`, ...args);
  }
  
  /**
   * Force an animation to play, bypassing priority checks
   * Use for critical state changes like death, respawn, etc.
   * 
   * @param {THREE.Group} playerGroup - The player model
   * @param {string} animationState - The animation state to force
   * @returns {boolean} Whether the animation was successfully played
   */
  forceAnimation(playerGroup, animationState) {
    if (!playerGroup || !playerGroup.userData) {
      return false;
    }
    
    const controller = playerGroup.userData.animController;
    if (!controller) {
      this.logError('No animation controller found on player');
      return false;
    }
    
    // Reset any locks
    controller.lockUntil = 0;
    
    // Get the actual animation name from the mapping
    const animName = playerGroup.userData.animationMap?.[animationState];
    
    // If no mapping found, try to find a fallback
    if (!animName) {
      this.logError(`No mapping found for animation state: ${animationState}`);
      const fallbackAnimation = this.findFallbackAnimation(playerGroup, animationState);
      if (fallbackAnimation) {
        this.logDebug(`Using fallback animation for forced state ${animationState}: ${fallbackAnimation}`);
        return this.playActualAnimation(playerGroup, fallbackAnimation, animationState);
      }
      return false;
    }
    
    // Play the mapped animation
    return this.playActualAnimation(playerGroup, animName, animationState);
  }
}

export default AnimationController; 